import { PlaceAutocomplete } from '@/components/Maps/PlaceAutocomplete';
import { CapabilitySelector } from '@/components/profile/CapabilitySelector';
import { ChannelExpertiseSelector } from '@/components/profile/ChannelExpertiseSelector';
import { CountrySelector } from '@/components/profile/CountrySelector';
import { RoleSelector } from '@/components/profile/RoleSelector';
import { SpecialisationSelector } from '@/components/profile/SpecialisationSelector';
import { HubLinker, parseHubIdsFromSystemTags } from '@/components/lookups/HubLinker';
import type { LookupOption } from '@/components/ui/lookup';
import type { ApiContent } from '@/lib/api/types';
import { ImageGalleryManager } from '@/components/ui/image-gallery-manager';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MediaSelector } from '@/components/ui/media-selector';
import { RegionSelector } from '@/components/ui/region-selector';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { MultiSelectDropdown } from '@/components/ui/multi-select-dropdown';
import type { ApiEntity } from '@/lib/api/types';
import { ENTITY_VISIBILITY_OPTIONS, NZ_REGIONS, INITIATIVE_THEME_OPTIONS } from '@sizzlenz/shared';
import { MapPin, X } from 'lucide-react';
import dynamic from 'next/dynamic';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { RichTextEditor } from '../ui/rich-text-editor';

const MDEditor = dynamic(() => import('@uiw/react-md-editor'), { ssr: false });

interface EntityFormData {
  name_1: string;
  slug?: string;
  description?: string;
  image?: string | null;
  website?: string;
  primary_email?: string;
  primary_phone?: string;
  instagram_url?: string;
  linkedin_url?: string;
  facebook_url?: string;
  tiktok_url?: string;
  roles?: string[];
  capabilities?: string[];
  specialisations?: string[];
  channelExpertise?: string[];
  geoPresence?: string[];
  geoExpertise?: string[];
  additionalDetails?: string;
  galleryImages?: string[];
  showInitiatives?: boolean;
  showItems?: boolean;
  showEnquiryForm?: boolean;
  showContactDetails?: boolean;
  showContentFeed?: boolean;
  contentFeedTitle?: string;
  itemsSectionTitle?: string;
  initiativesSectionTitle?: string;
  initiativesDescription?: string;
  allowPartnerProposals?: boolean;
  contactDetailsName?: string;
  contactDetailsEmail?: string;
  contactDetailsPhone?: string;
  trade_enabled?: boolean;
  hosts_activations?: boolean;
  runs_activations?: boolean;
  base_region?: string | null;
  regions_served?: string[];
  exportsTo?: string[];
  visibility?: string;
  address_1?: Record<string, unknown> | null;
  tags?: string;
  allowReactions?: boolean;
  allowComments?: boolean;
  themes?: string[];
  themeFeatured?: boolean;
  notes?: string;
  sidebarPosition?: string;
}

interface EntityFormProps {
  entity?: ApiEntity;
  onSubmit: (data: EntityFormData) => void | Promise<void>;
  showRoles?: boolean;
  /** When true, shows platform admin-only fields like notes */
  isPlatformAdmin?: boolean;
}

export interface EntityFormRef {
  submit: () => void;
  setError: (name: string, error: { type: string; message: string }) => void;
}

export const EntityForm = forwardRef<EntityFormRef, EntityFormProps>(
  ({ entity, onSubmit, showRoles = false, isPlatformAdmin = false }, ref) => {
    const {
      register,
      handleSubmit,
      setValue,
      setError,
      watch,
      getValues,
      control,
      formState: { errors },
    } = useForm<EntityFormData>({
      defaultValues: {
        name_1: entity?.name_1 || '',
        slug: entity?.slug || '',
        description: entity?.description || '',
        image: entity?.image || null,
        website: entity?.website || '',
        primary_email: entity?.primary_email || '',
        primary_phone: entity?.primary_phone || '',
        instagram_url:
          (entity?.data?.links as any[])?.find((l: any) => l.platform === 'instagram')?.url || '',
        linkedin_url:
          (entity?.data?.links as any[])?.find((l: any) => l.platform === 'linkedin')?.url || '',
        facebook_url:
          (entity?.data?.links as any[])?.find((l: any) => l.platform === 'facebook')?.url || '',
        tiktok_url:
          (entity?.data?.links as any[])?.find((l: any) => l.platform === 'tiktok')?.url || '',
        roles: (entity?.data?.roles as string[]) || [],
        capabilities: (entity?.data?.capabilities as string[]) || [],
        specialisations: (entity?.data?.specialisations as string[]) || [],
        channelExpertise: (entity?.data?.channelExpertise as string[]) || [],
        geoPresence: (entity?.data?.geoPresence as string[]) || [],
        geoExpertise: (entity?.data?.geoExpertise as string[]) || [],
        additionalDetails: '',
        galleryImages: (entity?.data?.gallery as any)?.images || [],
        showInitiatives: !!(entity?.data?.main_page as any)?.showInitiatives,
        showItems: !!(entity?.data?.main_page as any)?.showItems,
        showEnquiryForm: !!(entity?.data?.main_page as any)?.showEnquiryForm,
        showContactDetails: !!(entity?.data?.main_page as any)?.showContactDetails,
        showContentFeed: !!(entity?.data?.main_page as any)?.showContentFeed,
        contentFeedTitle: (entity?.data?.main_page as any)?.contentFeedTitle || '',
        itemsSectionTitle: (entity?.data?.items as any)?.sectionTitle || '',
        initiativesSectionTitle: (entity?.data?.initiatives as any)?.sectionTitle || '',
        initiativesDescription: (entity?.data?.initiatives as any)?.description || '',
        allowPartnerProposals: !!(entity?.data?.initiatives as any)?.allowPartnerProposals,
        contactDetailsName: (entity?.data?.contactDetails as any)?.name || '',
        contactDetailsEmail: (entity?.data?.contactDetails as any)?.email || '',
        contactDetailsPhone: (entity?.data?.contactDetails as any)?.phone || '',
        trade_enabled: !!(entity?.data?.trade as any)?.enabled,
        hosts_activations: !!(entity?.data?.activations as any)?.hosts,
        runs_activations: !!(entity?.data?.activations as any)?.runs,
        base_region: (entity?.data?.reporting as any)?.base_region || null,
        regions_served: (entity?.data?.reporting as any)?.regions_served || [],
        exportsTo: (entity?.data?.exports_to as string[]) || [],
        visibility: entity?.visibility || 'visible',
        address_1: (entity?.data?.address_1 as Record<string, unknown>) || null,
        tags: entity?.tags?.join(', ') || '',
        allowReactions: (entity?.data as any)?.allowReactions !== false,
        allowComments: (entity?.data as any)?.allowComments === true,
        themes: (entity?.data as any)?.themes || [],
        themeFeatured: (entity?.data as any)?.themeFeatured || false,
        notes: (entity?.systemData as any)?.notes || '',
        sidebarPosition: (entity?.data?.main_page as any)?.sidebarPosition || 'right',
      },
    });

    const imageValue = watch('image');

    // Hub linking state
    const [selectedHubs, setSelectedHubs] = useState<LookupOption<ApiContent>[]>(() => {
      const hubsCache = (entity?.data as any)?.hubs as
        | Array<{ id: number; title: string }>
        | undefined;
      if (hubsCache && hubsCache.length > 0) {
        return hubsCache.map((hub) => ({
          id: hub.id,
          label: hub.title || `Hub ${hub.id}`,
          data: { id: hub.id, title: hub.title } as unknown as ApiContent,
        }));
      }
      return [];
    });

    const handleFormSubmit = (data: EntityFormData) => {
      // Augment data with hubs before passing to onSubmit
      const augmented = {
        ...data,
        hubs: selectedHubs.map((h) => ({ id: h.id as number, title: h.label })),
      };
      return onSubmit(augmented as any);
    };

    // Expose submit and setError methods to parent via ref
    useImperativeHandle(ref, () => ({
      submit: () => {
        handleSubmit(handleFormSubmit)().catch(() => {
          // Errors are handled by the onSubmit callback's own try/catch
        });
      },
      setError: (name: string, error: { type: string; message: string }) => {
        setError(name as any, error);
      },
    }));
    return (
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
        {/* Image Upload */}
        <MediaSelector
          value={imageValue}
          onChange={(url) => setValue('image', typeof url === 'string' ? url : url?.[0] || null)}
          label="Image"
          size="md"
        />

        {/* Name */}
        <div>
          <Label htmlFor="name">
            Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="name"
            {...register('name_1', { required: 'Name is required' })}
            placeholder="Enter name"
          />
          {errors.name_1 && <p className="text-sm text-red-600 mt-1">{errors.name_1.message}</p>}
        </div>

        {/* Slug */}
        <div>
          <Label htmlFor="slug">Slug</Label>
          <Input
            id="slug"
            {...register('slug', {
              pattern: {
                value: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                message: 'Slug must be lowercase letters, numbers, and hyphens only',
              },
            })}
            placeholder="e.g. my-entity-name"
          />
          <p className="text-xs text-gray-500 mt-1">
            URL-friendly identifier. Leave blank to use the default.
          </p>
          {errors.slug && <p className="text-sm text-red-600 mt-1">{errors.slug.message}</p>}
        </div>

        {/* Visibility */}
        <div>
          <Label htmlFor="visibility">Visibility</Label>
          <Controller
            name="visibility"
            control={control}
            render={({ field }) => (
              <Select value={field.value || 'visible'} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select visibility" />
                </SelectTrigger>
                <SelectContent>
                  {ENTITY_VISIBILITY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        {/* Tags */}
        <div>
          <Label htmlFor="tags">Tags (comma separated)</Label>
          <Input id="tags" {...register('tags')} placeholder="e.g. organic, local, premium" />
        </div>

        {/* Bio with Markdown Editor */}
        <div>
          <Label htmlFor="bio">Bio</Label>
          <div data-color-mode="light">
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <RichTextEditor
                  value={field.value || ''}
                  onChange={(value) => field.onChange(value || '')}
                  height={200}
                  placeholder="Enter bio (supports markdown)"
                />
              )}
            />
          </div>
          <p className="text-xs text-gray-500">
            Supports markdown formatting. Use the toolbar or type markdown directly.
          </p>
        </div>

        {/* Website */}
        <div>
          <Label htmlFor="website">Website</Label>
          <Input
            id="website"
            type="url"
            {...register('website')}
            placeholder="https://example.com"
          />
        </div>

        {/* Email */}
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            {...register('primary_email')}
            placeholder="contact@example.com"
          />
        </div>

        {/* Phone */}
        <div>
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            type="tel"
            {...register('primary_phone')}
            placeholder="+1 234 567 8900"
          />
        </div>

        {/* Address */}
        <div>
          <Label>Address</Label>
          <Controller
            name="address_1"
            control={control}
            render={({ field }) => {
              const currentAddress = field.value as Record<string, unknown> | null;
              const displayAddress =
                currentAddress?.display_address || currentAddress?.formatted_address;

              if (displayAddress) {
                return (
                  <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                    <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="flex-1 truncate">{displayAddress as string}</span>
                    <button
                      type="button"
                      onClick={() => field.onChange(null)}
                      className="shrink-0 cursor-pointer text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                );
              }
              return (
                <PlaceAutocomplete
                  placeholder="Search for an address..."
                  onPlaceDetail={(detail) => {
                    if (detail) {
                      field.onChange(detail);
                    } else {
                      field.onChange(null);
                    }
                  }}
                />
              );
            }}
          />
        </div>

        {/* Contact Details (stored in entity.data.contactDetails) */}
        <div className="space-y-3 border-t pt-4">
          <div>
            <h3 className="font-medium">Contact Details</h3>
            <p className="text-sm text-gray-500">Additional contact information for this entity</p>
          </div>

          <div>
            <Label htmlFor="contactDetailsName">Contact Name</Label>
            <Input
              id="contactDetailsName"
              type="text"
              {...register('contactDetailsName')}
              placeholder="John Smith"
            />
          </div>

          <div>
            <Label htmlFor="contactDetailsEmail">Email</Label>
            <Input
              id="contactDetailsEmail"
              type="email"
              {...register('contactDetailsEmail')}
              placeholder="trade@example.com"
            />
          </div>

          <div>
            <Label htmlFor="contactDetailsPhone">Phone</Label>
            <Input
              id="contactDetailsPhone"
              type="tel"
              {...register('contactDetailsPhone')}
              placeholder="+1 (555) 123-4567"
            />
          </div>
        </div>

        {/* Social Media Links */}
        <div className="space-y-3 border-t pt-4">
          <div>
            <h3 className="font-medium">Social Media</h3>
            <p className="text-sm text-gray-500">Connect your social media profiles</p>
          </div>

          <div>
            <Label htmlFor="instagram" className="text-xs text-gray-600">
              Instagram URL
            </Label>
            <Input
              id="instagram"
              type="url"
              {...register('instagram_url')}
              placeholder="https://instagram.com/username"
            />
          </div>

          <div>
            <Label htmlFor="linkedin" className="text-xs text-gray-600">
              LinkedIn URL
            </Label>
            <Input
              id="linkedin"
              type="url"
              {...register('linkedin_url')}
              placeholder="https://linkedin.com/company/name"
            />
          </div>

          <div>
            <Label htmlFor="facebook" className="text-xs text-gray-600">
              Facebook URL
            </Label>
            <Input
              id="facebook"
              type="url"
              {...register('facebook_url')}
              placeholder="https://facebook.com/page"
            />
          </div>

          <div>
            <Label htmlFor="tiktok" className="text-xs text-gray-600">
              TikTok URL
            </Label>
            <Input
              id="tiktok"
              type="url"
              {...register('tiktok_url')}
              placeholder="https://tiktok.com/@username"
            />
          </div>
        </div>

        {/* Roles */}
        {showRoles && (
          <div className="space-y-3 border-t pt-4">
            <div>
              <h3 className="font-medium">Roles</h3>
              <p className="text-sm text-gray-500">Select the roles that describe this entity</p>
            </div>
            <Controller
              name="roles"
              control={control}
              render={({ field }) => (
                <Controller
                  name="additionalDetails"
                  control={control}
                  render={({ field: orgField }) => (
                    <RoleSelector
                      displayAs="select"
                      selectedRoles={field.value || []}
                      additionalDetails={orgField.value || ''}
                      onRolesChange={field.onChange}
                      onAdditionalDetailsChange={orgField.onChange}
                      variant="entity"
                    />
                  )}
                />
              )}
            />
          </div>
        )}

        {/* Capabilities */}
        {showRoles && (
          <div className="space-y-3 border-t pt-4">
            <div>
              <h3 className="font-medium">Capabilities</h3>
              <p className="text-sm text-gray-500">Select the capabilities this entity offers</p>
            </div>
            <Controller
              name="capabilities"
              control={control}
              render={({ field }) => (
                <CapabilitySelector
                  selectedIds={field.value || []}
                  onSelectionChange={field.onChange}
                />
              )}
            />
          </div>
        )}

        {/* Specialisations */}
        {showRoles && (
          <div className="space-y-3 border-t pt-4">
            <div>
              <h3 className="font-medium">Specialisations</h3>
              <p className="text-sm text-gray-500">Select the specialisations for this entity</p>
            </div>
            <Controller
              name="specialisations"
              control={control}
              render={({ field }) => (
                <SpecialisationSelector
                  selectedIds={field.value || []}
                  onSelectionChange={field.onChange}
                />
              )}
            />
          </div>
        )}

        {/* Channel Expertise */}
        {showRoles && (
          <div className="space-y-3 border-t pt-4">
            <div>
              <h3 className="font-medium">Channel Expertise</h3>
              <p className="text-sm text-gray-500">
                Select the channels this entity has expertise in
              </p>
            </div>
            <Controller
              name="channelExpertise"
              control={control}
              render={({ field }) => (
                <ChannelExpertiseSelector
                  selectedIds={field.value || []}
                  onSelectionChange={field.onChange}
                />
              )}
            />
          </div>
        )}

        {/* Country Presence */}
        {showRoles && (
          <div className="space-y-3 border-t pt-4">
            <div>
              <h3 className="font-medium">Country Presence</h3>
              <p className="text-sm text-gray-500">
                Countries where this entity has a physical or operational presence
              </p>
            </div>
            <Controller
              name="geoPresence"
              control={control}
              render={({ field }) => (
                <CountrySelector
                  label="Country Presence"
                  selectedIds={field.value || []}
                  onSelectionChange={field.onChange}
                />
              )}
            />
          </div>
        )}

        {/* Country Expertise */}
        {showRoles && (
          <div className="space-y-3 border-t pt-4">
            <div>
              <h3 className="font-medium">Country Expertise</h3>
              <p className="text-sm text-gray-500">
                Countries this entity has market knowledge or expertise in
              </p>
            </div>
            <Controller
              name="geoExpertise"
              control={control}
              render={({ field }) => (
                <CountrySelector
                  label="Country Expertise"
                  selectedIds={field.value || []}
                  onSelectionChange={field.onChange}
                />
              )}
            />
          </div>
        )}

        {/* Main Page Settings */}
        <div className="space-y-3 border-t pt-4">
          <div>
            <h3 className="font-medium">Main Page Settings</h3>
            <p className="text-sm text-gray-500">Configure what appears on the entity main page</p>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="showEnquiryForm" className="text-sm font-normal cursor-pointer">
                Show Enquiry Form
              </Label>
              <p className="text-xs text-gray-500">
                Display an enquiry form on the entity main page
              </p>
            </div>
            <Controller
              name="showEnquiryForm"
              control={control}
              render={({ field }) => (
                <Switch
                  id="showEnquiryForm"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </div>

          {/* Sidebar Position */}
          <div>
            <Label htmlFor="sidebarPosition" className="text-sm font-normal">
              Sidebar Position
            </Label>
            <p className="text-xs text-gray-500 mb-2">
              Where to display profile details (Country Presence, Expertise, etc.) and the Enquiry
              Form
            </p>
            <Controller
              name="sidebarPosition"
              control={control}
              render={({ field }) => (
                <Select value={field.value || 'right'} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select position" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="right">Right Side</SelectItem>
                    <SelectItem value="left">Left Side</SelectItem>
                    <SelectItem value="under-description">Under Description</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="showContactDetails" className="text-sm font-normal cursor-pointer">
                Show Contact Details
              </Label>
              <p className="text-xs text-gray-500">
                Display contact details on the entity main page
              </p>
            </div>
            <Controller
              name="showContactDetails"
              control={control}
              render={({ field }) => (
                <Switch
                  id="showContactDetails"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="showContentFeed" className="text-sm font-normal cursor-pointer">
                Show Content Feed
              </Label>
              <p className="text-xs text-gray-500">
                Display a news and content feed on the entity main page
              </p>
            </div>
            <Controller
              name="showContentFeed"
              control={control}
              render={({ field }) => (
                <Switch
                  id="showContentFeed"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </div>

          {watch('showContentFeed') && (
            <div>
              <Label htmlFor="contentFeedTitle" className="text-xs text-gray-600">
                Content Feed Title
              </Label>
              <Input
                id="contentFeedTitle"
                {...register('contentFeedTitle')}
                placeholder="e.g. News & Updates"
              />
            </div>
          )}
        </div>
        {/* Items */}
        <div className="space-y-3 border-t pt-4">
          <div>
            <h3 className="font-medium">Items</h3>
            <p className="text-sm text-gray-500">Configure the items/products section</p>
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="showInitiatives" className="text-sm font-normal cursor-pointer">
                Show Items Section
              </Label>
              <p className="text-xs text-gray-500">Display initiatives on the entity main page</p>
            </div>
            <Controller
              name="showItems"
              control={control}
              render={({ field }) => (
                <Switch id="showItems" checked={field.value} onCheckedChange={field.onChange} />
              )}
            />
          </div>

          <div>
            <Label htmlFor="itemsSectionTitle" className="text-xs text-gray-600">
              Section Title
            </Label>
            <Input
              id="itemsSectionTitle"
              {...register('itemsSectionTitle')}
              placeholder="e.g. Products"
            />
          </div>
        </div>

        {/* Initiatives */}
        <div className="space-y-3 border-t pt-4">
          <div>
            <h3 className="font-medium">Initiatives</h3>
            <p className="text-sm text-gray-500">Configure the initiatives section</p>
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="showInitiatives" className="text-sm font-normal cursor-pointer">
                Show Initiatives Section
              </Label>
              <p className="text-xs text-gray-500">Display initiatives on the entity main page</p>
            </div>
            <Controller
              name="showInitiatives"
              control={control}
              render={({ field }) => (
                <Switch
                  id="showInitiatives"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </div>

          <div>
            <Label htmlFor="initiativesSectionTitle" className="text-xs text-gray-600">
              Section Title
            </Label>
            <Input
              id="initiativesSectionTitle"
              {...register('initiativesSectionTitle')}
              placeholder="e.g. Our Initiatives"
            />
          </div>

          <div>
            <Label htmlFor="initiativesDescription" className="text-xs text-gray-600">
              Description
            </Label>
            <Input
              id="initiativesDescription"
              {...register('initiativesDescription')}
              placeholder="Brief description of the initiatives section"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="allowPartnerProposals" className="text-sm font-normal cursor-pointer">
                Allow Proposals from Partners
              </Label>
              <p className="text-xs text-gray-500">
                Let partners propose initiatives for this entity. The Trade Hub must also be
                enabled.
              </p>
            </div>
            <Controller
              name="allowPartnerProposals"
              control={control}
              render={({ field }) => (
                <Switch
                  id="allowPartnerProposals"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </div>
        </div>

        {/* Trade Settings */}
        <div className="space-y-3 border-t pt-4">
          <div>
            <h3 className="font-medium">Trade</h3>
            <p className="text-sm text-gray-500">Configure trade-related settings</p>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="tradeEnabled" className="text-sm font-normal cursor-pointer">
                Trade Enabled
              </Label>
              <p className="text-xs text-gray-500">Enable trade functionality for this entity</p>
            </div>
            <Controller
              name="trade_enabled"
              control={control}
              render={({ field }) => (
                <Switch id="tradeEnabled" checked={field.value} onCheckedChange={field.onChange} />
              )}
            />
          </div>
        </div>

        {/* Activations Settings */}
        <div className="space-y-3 border-t pt-4">
          <div>
            <h3 className="font-medium">Activations</h3>
            <p className="text-sm text-gray-500">Configure activation-related settings</p>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="hostsActivations" className="text-sm font-normal cursor-pointer">
                Hosts Activations
              </Label>
              <p className="text-xs text-gray-500">This entity hosts activation events</p>
            </div>
            <Controller
              name="hosts_activations"
              control={control}
              render={({ field }) => (
                <Switch
                  id="hostsActivations"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="runsActivations" className="text-sm font-normal cursor-pointer">
                Runs Activations
              </Label>
              <p className="text-xs text-gray-500">This entity runs/organizes activation events</p>
            </div>
            <Controller
              name="runs_activations"
              control={control}
              render={({ field }) => (
                <Switch
                  id="runsActivations"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </div>
        </div>

        {/* Gallery Media */}
        <div className="space-y-3 border-t pt-4">
          <Controller
            name="galleryImages"
            control={control}
            render={({ field }) => (
              <ImageGalleryManager
                images={field.value || []}
                onImagesChange={field.onChange}
                title="Gallery Media"
                description="Add images to showcase your entity"
                emptyStateMessage="No images added yet"
                gridCols={3}
              />
            )}
          />
        </div>

        {/* Reporting Section */}
        <div className="space-y-3 border-t pt-4">
          <div>
            <h3 className="font-medium">Reporting</h3>
            <p className="text-sm text-gray-500">Configure regional information for reporting</p>
          </div>

          <div>
            <Label htmlFor="baseRegion" className="text-sm font-normal">
              Based/Primary Region
            </Label>
            <p className="text-xs text-gray-500 mb-2">Select the primary region for this entity</p>
            <Controller
              name="base_region"
              control={control}
              render={({ field }) => (
                <Select value={field.value || undefined} onValueChange={field.onChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select primary region" />
                  </SelectTrigger>
                  <SelectContent>
                    {NZ_REGIONS.map((region) => (
                      <SelectItem key={region.id} value={region.id}>
                        {region.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div>
            <Label htmlFor="regionsServed" className="text-sm font-normal">
              Regions Served
            </Label>
            <p className="text-xs text-gray-500 mb-2">
              Select all regions where this entity operates
            </p>
            <Controller
              name="regions_served"
              control={control}
              render={({ field }) => (
                <RegionSelector
                  selectedRegions={field.value || []}
                  onRegionsChange={field.onChange}
                  placeholder="Select regions served"
                  multiple={true}
                  label={null}
                  showChips={true}
                  showBulkActions={true}
                />
              )}
            />
          </div>
        </div>

        {/* Engagement Settings */}
        <div className="space-y-4 border-t pt-4">
          <h3 className="font-medium">Engagement</h3>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="allowReactions" className="text-sm font-normal cursor-pointer">
                Reactions
              </Label>
              <p className="text-xs text-gray-500">Allow users to react to this entity</p>
            </div>
            <Controller
              name="allowReactions"
              control={control}
              render={({ field }) => (
                <Switch
                  id="allowReactions"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="allowComments" className="text-sm font-normal cursor-pointer">
                Comments
              </Label>
              <p className="text-xs text-gray-500">Enable commenting on this entity</p>
            </div>
            <Controller
              name="allowComments"
              control={control}
              render={({ field }) => (
                <Switch id="allowComments" checked={field.value} onCheckedChange={field.onChange} />
              )}
            />
          </div>
        </div>

        {/* Export Markets */}
        <div className="space-y-3 border-t pt-4">
          <div>
            <h3 className="font-medium">Export Markets</h3>
            <p className="text-sm text-gray-500">Specify countries where this entity exports to</p>
          </div>

          <div>
            <Controller
              name="exportsTo"
              control={control}
              render={({ field }) => (
                <CountrySelector
                  label="Export Markets"
                  selectedIds={field.value || []}
                  onSelectionChange={field.onChange}
                  placeholder="Select export countries..."
                />
              )}
            />
          </div>
        </div>

        {/* Themes */}
        <div className="space-y-3 border-t pt-4">
          <div>
            <h3 className="font-medium">Themes</h3>
            <p className="text-sm text-gray-500">Associate this entity with theme pages</p>
          </div>

          <Controller
            name="themes"
            control={control}
            render={({ field }) => (
              <MultiSelectDropdown
                label={null}
                placeholder="Select themes..."
                options={INITIATIVE_THEME_OPTIONS.map((theme) => ({
                  id: theme.id,
                  label: theme.label,
                }))}
                selectedIds={field.value || []}
                onSelectionChange={field.onChange}
                showChips={true}
                showSummaryInTrigger={true}
                showBulkActions={false}
              />
            )}
          />

          <div className="flex items-center justify-between pt-2">
            <div className="space-y-0.5">
              <Label htmlFor="themeFeatured" className="text-sm font-normal cursor-pointer">
                Featured in Theme Page
              </Label>
              <p className="text-xs text-gray-500">
                Show this entity prominently on its theme page
              </p>
            </div>
            <Controller
              name="themeFeatured"
              control={control}
              render={({ field }) => (
                <Switch id="themeFeatured" checked={field.value} onCheckedChange={field.onChange} />
              )}
            />
          </div>
        </div>

        {/* Linked Hubs */}
        <div className="space-y-3 border-t pt-4">
          <HubLinker
            selectedHubs={selectedHubs}
            onSelectedHubsChange={setSelectedHubs}
            description="Link this entity to hubs so it appears in those hub views"
          />
        </div>

        {/* Platform Admin: Notes */}
        {isPlatformAdmin && (
          <div className="space-y-3 border-t pt-4">
            <div>
              <h3 className="font-medium">Platform Notes</h3>
              <p className="text-sm text-gray-500">
                Internal notes visible only to platform administrators
              </p>
            </div>
            <div data-color-mode="light">
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <MDEditor
                    value={field.value || ''}
                    onChange={(value) => field.onChange(value || '')}
                    preview="live"
                    height={150}
                    textareaProps={{
                      placeholder: 'Enter internal notes (supports markdown)',
                    }}
                  />
                )}
              />
            </div>
          </div>
        )}
      </form>
    );
  }
);

EntityForm.displayName = 'EntityForm';
